import { useEffect, useMemo, useState } from 'react';
import { PlusIcon, TrashIcon, ArrowsUpDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { metadataApi } from '@/services/api';

// Grid de categorías del catálogo (VIDA-371, procedimiento «Catálogo de
// producto»): cada categoría tiene imagen, estado, nivel, categoría interna del
// árbol comercial, etiquetas y a dónde lleva al pulsarla.

export type LinkTarget =
  | { type: 'product'; slug: string }
  | { type: 'parent_category'; slug: string }
  | { type: 'child_category'; parentSlug: string; slug: string }
  | { type: 'filter'; tags?: string[]; attributes?: Record<string, string> }
  | { type: 'url'; href: string };

export const CATEGORY_TIERS = ['premium', 'principal', 'estandar', 'apoyo'] as const;
export type CategoryTier = (typeof CATEGORY_TIERS)[number];

export interface CategoryTile {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  image?: string;
  description?: string;
  status?: 'active' | 'draft';
  tier?: CategoryTier;
  internal_category?: { tree: string; node: string; name: string } | null;
  tags?: string[];
  link?: LinkTarget;
}

// Topes del documento: 4 premium + 6 principales (10 en portada) y 10 de apoyo.
const TIER_LIMITS: Record<CategoryTier, number | null> = { premium: 4, principal: 6, estandar: null, apoyo: 10 };
const TIER_LABELS: Record<CategoryTier, string> = {
  premium: 'Premium',
  principal: 'Principal',
  estandar: 'Estándar (solo en «todas las categorías»)',
  apoyo: 'De apoyo (etiquetas para filtrar)',
};

// Sin nivel guardado (catálogos anteriores) cuenta como principal: así se ven como siempre.
export const tierOf = (c: CategoryTile): CategoryTier => c.tier ?? 'principal';

type Destination = 'default' | 'filter' | 'product' | 'subcategory' | 'shop' | 'url';

const DESTINATIONS: { value: Destination; label: string }[] = [
  { value: 'default', label: 'Como hasta ahora (por su slug)' },
  { value: 'filter', label: 'Listado de enlaces con sus etiquetas' },
  { value: 'subcategory', label: 'Página de la categoría (subcategoría)' },
  { value: 'product', label: 'Ficha de un enlace concreto' },
  { value: 'shop', label: 'Tienda online, filtrada por su etiqueta' },
  { value: 'url', label: 'Otra web (URL)' },
];

// La tienda (corestore) filtra por una sola etiqueta: ?tag=<etiqueta>.
const SHOP_MARK = '/productos?tag=';

function destinationOf(link?: LinkTarget): Destination {
  if (!link) return 'default';
  if (link.type === 'filter') return 'filter';
  if (link.type === 'product') return 'product';
  if (link.type === 'parent_category' || link.type === 'child_category') return 'subcategory';
  if (link.type === 'url') return link.href.includes(SHOP_MARK) ? 'shop' : 'url';
  return 'default';
}

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const smallLabel = 'block text-xs text-gray-500 mb-0.5';

interface CommercialTree {
  _id: string;
  id: string;
  name: string;
  organization_id?: string | null;
}
interface TreeNode {
  _id: string;
  id: string;
  name: string;
  depth?: number;
}

/** Árboles comerciales visibles para la organización: los propios primero. */
function useCommercialTrees() {
  const [trees, setTrees] = useState<CommercialTree[]>([]);
  const [nodes, setNodes] = useState<Record<string, TreeNode[]>>({});

  useEffect(() => {
    metadataApi
      .getTrees(undefined, true)
      .then((res) => {
        const all: CommercialTree[] = res.data?.data || res.data?.trees || [];
        const commercial = all.filter((t) => t.id.includes('comercial'));
        commercial.sort((a, b) => Number(!!b.organization_id) - Number(!!a.organization_id));
        setTrees(commercial);
      })
      .catch(() => setTrees([]));
  }, []);

  const loadNodes = (tree: CommercialTree) => {
    if (nodes[tree.id]) return;
    metadataApi
      .getNodes(tree._id)
      .then((res) => {
        const payload = res.data?.data ?? res.data?.nodes ?? res.data;
        setNodes((prev) => ({ ...prev, [tree.id]: Array.isArray(payload) ? payload : [] }));
      })
      .catch(() => setNodes((prev) => ({ ...prev, [tree.id]: [] })));
  };

  return { trees, nodes, loadNodes };
}

function TagsInput({ value, onChange }: { value: string[]; onChange: (tags: string[]) => void }) {
  const [text, setText] = useState(value.join(', '));
  useEffect(() => setText(value.join(', ')), [value]);
  return (
    <input
      type="text"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(Array.from(new Set(text.split(',').map((t) => t.trim()).filter(Boolean))))}
      className={inputClass}
      placeholder="senderismo, familias"
    />
  );
}

interface TileProps {
  cat: CategoryTile;
  index: number;
  total: number;
  onChange: (next: CategoryTile) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  trees: CommercialTree[];
  nodes: Record<string, TreeNode[]>;
  loadNodes: (tree: CommercialTree) => void;
  ImageField: (props: { value: string; onChange: (url: string) => void }) => JSX.Element;
}

function CategoryTileCard({ cat, index, total, onChange, onRemove, onMove, trees, nodes, loadNodes, ImageField }: TileProps) {
  const set = <K extends keyof CategoryTile>(field: K, value: CategoryTile[K]) => onChange({ ...cat, [field]: value });
  const dest = destinationOf(cat.link);
  const tags = cat.tags ?? [];
  const selectedTree = trees.find((t) => t.id === cat.internal_category?.tree) || trees[0];

  useEffect(() => {
    if (selectedTree) loadNodes(selectedTree);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTree?.id]);

  const setDestination = (d: Destination) => {
    switch (d) {
      case 'default': return set('link', undefined);
      case 'filter': return set('link', { type: 'filter', tags: tags.length ? tags : [cat.slug].filter(Boolean) });
      case 'subcategory': return set('link', { type: 'parent_category', slug: cat.slug });
      case 'product': return set('link', { type: 'product', slug: '' });
      case 'shop': return set('link', { type: 'url', href: `${SHOP_MARK}${encodeURIComponent(tags[0] || cat.slug)}` });
      case 'url': return set('link', { type: 'url', href: '' });
    }
  };

  // La URL de la tienda se rehace con su dirección y la primera etiqueta.
  const shopBase = dest === 'shop' && cat.link?.type === 'url' ? cat.link.href.split(SHOP_MARK)[0] : '';
  const setShop = (base: string, tag: string) =>
    set('link', { type: 'url', href: `${base.replace(/\/+$/, '')}${SHOP_MARK}${encodeURIComponent(tag)}` });

  const setTags = (next: string[]) => {
    const updated: CategoryTile = { ...cat, tags: next };
    if (cat.link?.type === 'filter') updated.link = { ...cat.link, tags: next };
    if (dest === 'shop') updated.link = { type: 'url', href: `${shopBase.replace(/\/+$/, '')}${SHOP_MARK}${encodeURIComponent(next[0] || cat.slug)}` };
    onChange(updated);
  };

  return (
    <div className={`border rounded-lg p-4 space-y-3 ${cat.status === 'draft' ? 'border-dashed border-gray-300 bg-gray-50' : 'border-gray-200 bg-gray-50/50'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Categoría {index + 1}{cat.status === 'draft' ? ' · borrador' : ''}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" aria-label="Subir">
            <ArrowsUpDownIcon className="w-4 h-4 rotate-180" />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" aria-label="Bajar">
            <ArrowsUpDownIcon className="w-4 h-4" />
          </button>
          <button onClick={onRemove} className="p-1 text-red-400 hover:text-red-600" aria-label="Eliminar categoría">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <div>
          <label className={smallLabel}>Nombre comercial</label>
          <input type="text" value={cat.name} onChange={(e) => set('name', e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={smallLabel}>Slug</label>
          <input type="text" value={cat.slug} onChange={(e) => set('slug', e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={smallLabel}>Estado</label>
          <select value={cat.status ?? 'active'} onChange={(e) => set('status', e.target.value as 'active' | 'draft')} className={inputClass}>
            <option value="active">Activa</option>
            <option value="draft">Borrador (no se ve)</option>
          </select>
        </div>
        <div>
          <label className={smallLabel}>Nivel</label>
          <select value={tierOf(cat)} onChange={(e) => set('tier', e.target.value as CategoryTier)} className={inputClass}>
            {CATEGORY_TIERS.map((t) => <option key={t} value={t}>{TIER_LABELS[t]}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={smallLabel}>Descripción</label>
        <textarea value={cat.description ?? ''} onChange={(e) => set('description', e.target.value)} rows={2} className={inputClass} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-1">
          <label className={smallLabel}>Imagen</label>
          <ImageField value={cat.image ?? ''} onChange={(url) => set('image', url)} />
        </div>
        <div>
          <label className={smallLabel}>Icono (Lucide)</label>
          <input type="text" value={cat.icon} onChange={(e) => set('icon', e.target.value)} className={inputClass} placeholder="Mountain" />
        </div>
        <div>
          <label className={smallLabel}>Gradiente</label>
          <input type="text" value={cat.color} onChange={(e) => set('color', e.target.value)} className={inputClass} placeholder="from-amber-500 to-orange-600" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className={smallLabel}>Categoría interna (árbol comercial)</label>
          {trees.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">La organización no tiene árbol comercial en Metadatos.</p>
          ) : (
            <div className="flex gap-2">
              {trees.length > 1 && (
                <select
                  value={selectedTree?.id ?? ''}
                  onChange={(e) => {
                    const tree = trees.find((t) => t.id === e.target.value);
                    if (tree) loadNodes(tree);
                    set('internal_category', null);
                  }}
                  className={inputClass}
                >
                  {trees.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              <select
                value={cat.internal_category?.node ?? ''}
                onChange={(e) => {
                  const node = (nodes[selectedTree!.id] || []).find((n) => n.id === e.target.value);
                  set('internal_category', node ? { tree: selectedTree!.id, node: node.id, name: node.name } : null);
                }}
                className={inputClass}
              >
                <option value="">Sin categoría interna</option>
                {(nodes[selectedTree?.id ?? ''] || []).map((n) => (
                  <option key={n.id} value={n.id}>{'— '.repeat(n.depth ?? 0)}{n.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div>
          <label className={smallLabel}>Etiquetas (separadas por comas)</label>
          <TagsInput value={tags} onChange={setTags} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className={smallLabel}>Al pulsar lleva a</label>
          <select value={dest} onChange={(e) => setDestination(e.target.value as Destination)} className={inputClass}>
            {DESTINATIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          {dest === 'product' && cat.link?.type === 'product' && (
            <>
              <label className={smallLabel}>Slug del enlace</label>
              <input type="text" value={cat.link.slug} onChange={(e) => set('link', { type: 'product', slug: e.target.value.trim() })} className={inputClass} />
            </>
          )}
          {dest === 'url' && cat.link?.type === 'url' && (
            <>
              <label className={smallLabel}>URL</label>
              <input type="url" value={cat.link.href} onChange={(e) => set('link', { type: 'url', href: e.target.value.trim() })} className={inputClass} placeholder="https://..." />
            </>
          )}
          {dest === 'shop' && (
            <>
              <label className={smallLabel}>Dirección de la tienda (filtra por «{tags[0] || cat.slug}»)</label>
              <input type="url" value={shopBase} onChange={(e) => setShop(e.target.value.trim(), tags[0] || cat.slug)} className={inputClass} placeholder="https://tienda.ejemplo.es" />
            </>
          )}
          {dest === 'filter' && (
            <p className="text-xs text-gray-500 pt-5">
              {tags.length ? `Muestra los enlaces con: ${tags.join(', ')}` : 'Añade etiquetas para filtrar.'}
            </p>
          )}
          {dest === 'subcategory' && <p className="text-xs text-gray-500 pt-5">Página de la categoría «{cat.slug}».</p>}
        </div>
      </div>
    </div>
  );
}

export default function CatalogCategoriesEditor({ categories, onChange, ImageField, newId }: {
  categories: CategoryTile[];
  onChange: (c: CategoryTile[]) => void;
  ImageField: TileProps['ImageField'];
  newId: () => string;
}) {
  const { trees, nodes, loadNodes } = useCommercialTrees();

  const counts = useMemo(() => {
    const active = categories.filter((c) => (c.status ?? 'active') === 'active');
    return Object.fromEntries(CATEGORY_TIERS.map((t) => [t, active.filter((c) => tierOf(c) === t).length])) as Record<CategoryTier, number>;
  }, [categories]);
  const overLimits = CATEGORY_TIERS.filter((t) => TIER_LIMITS[t] !== null && counts[t] > (TIER_LIMITS[t] as number));

  const replace = (idx: number, next: CategoryTile) => onChange(categories.map((c, i) => (i === idx ? next : c)));
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };
  const add = () =>
    onChange([...categories, { id: newId(), name: '', slug: '', icon: 'Star', color: 'from-gray-500 to-gray-600', status: 'active', tier: 'principal', tags: [] }]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        En portada salen las premium y las principales (recomendado: 4 + 6). Las estándar aparecen en «todas las categorías» y las de apoyo como etiquetas para filtrar.
        Activas ahora: {counts.premium} premium · {counts.principal} principales · {counts.estandar} estándar · {counts.apoyo} de apoyo.
      </p>
      {overLimits.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
          <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
          <span>
            Pasas del tope recomendado en: {overLimits.map((t) => `${TIER_LABELS[t].split(' (')[0].toLowerCase()} (${counts[t]}/${TIER_LIMITS[t]})`).join(', ')}.
            Se puede guardar, pero satura al visitante.
          </span>
        </div>
      )}
      {categories.map((cat, idx) => (
        <CategoryTileCard
          key={cat.id}
          cat={cat}
          index={idx}
          total={categories.length}
          onChange={(next) => replace(idx, next)}
          onRemove={() => onChange(categories.filter((_, i) => i !== idx))}
          onMove={(dir) => move(idx, dir)}
          trees={trees}
          nodes={nodes}
          loadNodes={loadNodes}
          ImageField={ImageField}
        />
      ))}
      <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50 transition-colors">
        <PlusIcon className="w-4 h-4" /> Añadir categoría
      </button>
    </div>
  );
}
